/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { useHistory, useRouteMatch } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { isUserManagerByInitiativeId } from '../../selectors/user';
import { hasSubscriptionSetup, isSingleOrg } from '../../selectors/initiative';
import { generateUrl } from '../../routes/util';
import './styles.scss';
import { useAppSelector } from '../../reducers';
import { AdminSettingsCard } from './AdminSettingsCard';
import Dashboard, { DashboardRow } from '../../components/dashboard';
import NotAuthorised from '../../routes/not-authorised';
import { getBaseRoute } from '../custom-metrics/CustomMetricContainer';
import { FeaturePermissions } from '../../services/permissions/FeaturePermissions';
import { getBaseCompanySettingsRoute } from '../../routes/company-tracker/CompanySettingsRoute';
import { isPortfolioTrackerExchange } from '../../selectors/portfolio';

interface CardSettings {
  isRendered: boolean;
  handler: () => void;
  icon: string;
  text: string;
  disabledText?: string;
}

const getBaseManageUserRoute = (isPortfolioTracker: boolean | undefined) => {
  return isPortfolioTracker ? ROUTES.PORTFOLIO_TRACKER_MANAGE_USERS : ROUTES.MANAGE_USERS;
}

const AdminSettings = () => {
  const history = useHistory();
  const match = useRouteMatch<{ initiativeId?: string; portfolioId?: string }>();
  const { initiativeId, portfolioId } = match.params;
  const isCompanyTracker = !!initiativeId;
  const isPortfolioTracker = !!portfolioId;
  const isManager = useAppSelector((state) =>
    isUserManagerByInitiativeId(state, isCompanyTracker ? initiativeId : portfolioId)
  );
  const isExchangeView = useAppSelector(isPortfolioTrackerExchange);

  const hasSubscriptions = useAppSelector(hasSubscriptionSetup);
  const limitReportingLevels = useAppSelector(FeaturePermissions.getLimitReportingLevels);
  const isSingleOrganisation = useAppSelector(isSingleOrg);

  if ((!isCompanyTracker && !isPortfolioTracker) || !isManager) {
    return <NotAuthorised />;
  }

  const settings: CardSettings[] = [{
    isRendered: !isPortfolioTracker,
    handler: () => history.push(generateUrl(ROUTES.ADMIN_DASHBOARD, match.params)),
    icon: 'fal fa-chart-line-up fa-3x',
    text: 'Admin dashboard',
    disabledText:
    limitReportingLevels === 1 && isSingleOrganisation
      ? 'Add additional subsidiaries to your organisation map to enable this feature'
      : '',
  },
  {
    isRendered: true,
    handler: () => history.push(generateUrl(getBaseManageUserRoute(isPortfolioTracker), match.params)),
    icon: 'fal fa-user-gear fa-3x',
    text: 'Manage users',
  },
  {
    isRendered: isCompanyTracker || isExchangeView,
    handler: () => history.push(generateUrl(getBaseCompanySettingsRoute(isPortfolioTracker), match.params)),
    icon: 'fal fa-sliders fa-3x',
    text: 'Company settings',
  },
  {
    isRendered: true,
    handler: () => history.push(generateUrl(getBaseRoute(isPortfolioTracker),match.params)),
    icon: 'fal fa-file-circle-question fa-3x',
    text: isPortfolioTracker ? 'Assigned metric packs' : 'Custom metrics',
  },
  {
    isRendered: isPortfolioTracker,
    handler: () => history.push(generateUrl(ROUTES.PORTFOLIO_TRACKER_MANAGE_SPONSORSHIPS, { portfolioId })),
    icon: 'fal fa-magnifying-glass-dollar fa-3x',
    text: 'Manage sponsorships',
  },
  {
    isRendered: !isPortfolioTracker,
    handler: () => history.push(generateUrl(ROUTES.DATA_SHARE_INITIATIVE, match.params)),
    icon: 'fa fa-share fa-3x',
    text: 'Data sharing',
  },
  {
    isRendered: !isPortfolioTracker,
    handler: () => history.push(generateUrl(ROUTES.SYSTEM_LOG, match.params)),
    icon: 'fal fa-file-waveform fa-3x',
    text: 'System logs',
  },
  {
    isRendered: !!hasSubscriptions && !!initiativeId,
    handler: () => history.push(generateUrl(ROUTES.REFERRALS, { initiativeId })),
    icon: 'fal fa-rectangle-barcode fa-3x',
    text: 'Referral code',
  },
  {
    isRendered: isCompanyTracker,
    handler: () => history.push(generateUrl(ROUTES.BULK_IMPORTING, { initiativeId })),
    icon: 'fal fa-file-import fa-3x',
    text: 'Bulk importing',
  },
  {
    isRendered: isCompanyTracker,
    handler: () => history.push(generateUrl(ROUTES.SUBSCRIPTION_INFO, { initiativeId })),
    icon: 'fal fa-box-check fa-3x',
    text: 'Subscription info',
  },
  ];

  return (
    <div>
      <Dashboard hasSidebar={false}>
        <DashboardRow>
          <h3>Admin settings</h3>
        </DashboardRow>
        <div className='admin-settings-container'>
          {settings.map(
            (setting) =>
              setting.isRendered && (
                <AdminSettingsCard
                  key={setting.text}
                  onClickHandler={setting.handler}
                  icon={setting.icon}
                  text={setting.text}
                  disabledText={setting.disabledText}
                />
              )
          )}
        </div>
      </Dashboard>
    </div>
  );
};

export default AdminSettings;
